/**
 * Unit normalization + conversion helpers — mirror of
 * backend/routes/inventory.py::_normalize_unit / _convert_qty.
 * Keeping the logic identical on both sides avoids silent
 * divergence when the chef types "200 g" and the inventory
 * is canonical "kg".
 */

const UNIT_ALIASES = {
  gr: "g", grs: "g", gramo: "g", gramos: "g",
  kilo: "kg", kilos: "kg", kilogramo: "kg", kilogramos: "kg",
  mililitro: "ml", mililitros: "ml", cc: "ml",
  litro: "l", litros: "l",
  unidad: "ud", unidades: "ud", u: "ud", und: "ud",
};

export function normalizeUnit(u) {
  if (!u) return "";
  const s = String(u).trim().toLowerCase().replace(/\.+$/, "");
  return UNIT_ALIASES[s] ?? s;
}

/**
 * Returns { qty: number, ok: boolean }.
 * - ok=true if units are equal, one is empty, or the pair is
 *   a supported metric conversion (g↔kg, ml↔l).
 * - ok=false for incompatible pairs (e.g. ud↔kg) — qty returned unchanged
 *   so the caller can decide to demote to a suggestion.
 */
export function convertQty(qty, fromUnit, toUnit) {
  const fu = normalizeUnit(fromUnit);
  const tu = normalizeUnit(toUnit);
  if (fu === tu || !fu || !tu) return { qty, ok: true };
  if (fu === "g" && tu === "kg") return { qty: qty / 1000, ok: true };
  if (fu === "kg" && tu === "g") return { qty: qty * 1000, ok: true };
  if (fu === "ml" && tu === "l") return { qty: qty / 1000, ok: true };
  if (fu === "l" && tu === "ml") return { qty: qty * 1000, ok: true };
  return { qty, ok: false };
}

/**
 * Single source of truth for ingredient cost calculation.
 * Mirror of backend helpers.calculate_recipe_cost for a single row.
 *
 *   cost = cost_per_unit × quantity × (1 + waste%)
 *
 * `row` is an ingredient row (`{quantity, unit, ...}`). `inventory` is
 * the linked inventory doc (`{cost_per_unit, unit, waste_percentage}`) or
 * null when the row is not linked.
 *
 * Returns 0 if inventory has no cost_per_unit (so callers can decide to
 * keep the existing estimated_cost via the `_cost_missing` flag upstream).
 */
export function computeIngredientCost(row, inventory) {
  if (!row || !inventory) return 0;
  const cpu = parseFloat(inventory.cost_per_unit);
  if (!cpu || cpu <= 0) return 0;
  const qtyRaw = parseFloat(row.quantity) || 0;
  // Convert row qty into inventory's unit so cost is computed with
  // a consistent reference (mirrors backend `auto_link_recipe_core`).
  const { qty: qtyBase } = convertQty(qtyRaw, row.unit, inventory.unit);
  const wasteMult = 1 + (parseFloat(inventory.waste_percentage) || 0) / 100;
  return cpu * qtyBase * wasteMult;
}
